import Loader2 from 'lucide-react/dist/esm/icons/loader-2'
import Lock from 'lucide-react/dist/esm/icons/lock'
import Plus from 'lucide-react/dist/esm/icons/plus'

import { cn } from '@/lib/utils'
import { StripeWordmark } from '@/components/dashboard/StripeWordmark'

type StripeConnectButtonProps = {
  onClick: () => void
  disabled?: boolean
  isLoading?: boolean
  variant?: 'primary' | 'secondary'
  label?: 'connect' | 'add-account'
  className?: string
}

const LABELS = {
  connect: 'Link bank account with',
  'add-account': 'Add another account with',
} as const

/**
 * Stripe-branded CTA for Financial Connections. Clean white surface with a purple stripe
 * wordmark accent — secure and recognizable without a heavy blurple fill.
 */
export function StripeConnectButton({
  onClick,
  disabled = false,
  isLoading = false,
  variant = 'primary',
  label = 'connect',
  className,
}: StripeConnectButtonProps) {
  const isPrimary = variant === 'primary'

  return (
    <button
      type="button"
      disabled={disabled || isLoading}
      onClick={onClick}
      className={cn(
        'group relative inline-flex w-full items-center justify-center gap-2.5 rounded-xl px-4 text-sm font-semibold tracking-tight transition-all duration-150 ease-out',
        'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[#635BFF]/15',
        'disabled:pointer-events-none disabled:opacity-55',
        isPrimary
          ? 'h-12 border border-[#635BFF]/25 bg-[#635BFF]/[0.06] text-foreground shadow-[0_2px_10px_rgba(99,91,255,0.1),0_1px_3px_rgba(0,0,0,0.05)] hover:border-[#635BFF]/35 hover:bg-[#635BFF]/[0.1] hover:shadow-[0_4px_14px_rgba(99,91,255,0.14),0_2px_4px_rgba(0,0,0,0.06)] active:scale-[0.99]'
          : 'h-10 border border-[#635BFF]/20 bg-white text-foreground shadow-[0_1px_4px_rgba(0,0,0,0.04)] hover:border-[#635BFF]/30 hover:bg-[#635BFF]/[0.04] active:scale-[0.99]',
        className,
      )}
    >
      {isLoading ? (
        <>
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="text-muted-foreground">Opening secure Stripe sign-in…</span>
        </>
      ) : (
        <>
          {isPrimary ? (
            <Lock className="h-4 w-4 shrink-0 text-success-border" aria-hidden="true" />
          ) : (
            <Plus className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          <span className="flex items-baseline gap-1.5">
            <span>{LABELS[label]}</span>
            <StripeWordmark className="text-[1.05em] leading-none text-[#635BFF]" />
          </span>
        </>
      )}
    </button>
  )
}
